/* global ol */
import ExtremeMapLandmarkLayerManager from '@/lib/ExtremeMapLandmarkLayerManager'
import * as EVT from '@/consts/eventNames'
import Stream from '@/lib/Stream'

export default class ExtremeMapPoleLayerManager extends ExtremeMapLandmarkLayerManager {
  constructor() {
    super()
    this.layerInfo.onPoleClick = null
    this.selectedPoles = []
    this.setStream(new Stream({ name: 'poleLayerDefault', debug: false }))
  }

  getColorMap_() {
    const fill = {
      orange: 'rgba(238, 124, 0, 0.8)',
      green: 'rgba(14, 204, 0, 0.8)',
      gray: 'rgba(80, 80, 80, 0.8)',
    }
    const stroke = {
      orange: 'rgba(180, 100, 0, 0.9)',
      green: 'rgba(10, 153, 0, 0.9)',
      gray: 'rgba(30, 30, 30, 0.8)',
    }
    return {
      point: {
        fill: Object.assign({}, fill),
        stroke: Object.assign({}, stroke),
      }
    }
  }

  getDefaultColor_(pole) {
    return pole.pole_number ? 'gray' : 'green'
  }

  getNewPointStyle_({ color, sizeType }) {
    const colorMap = this.getColorMap_()
    const fillColor = colorMap.point.fill[color]
    const strokeColor = colorMap.point.stroke[color]
    let radius = 3
    if (sizeType === 'big') { radius = 8 }
    return new ol.style.Style({
      image: new ol.style.Circle({
        radius: radius,
        fill: new ol.style.Fill({
          color: fillColor,
        }),
        stroke: new ol.style.Stroke({
          color: strokeColor,
          width: 1
        })
      }),
    })
  }

  setStyle_(poleId, { selected }) {
    const { p1 } = this.getFeaturesByPoleId_(poleId)
    if (!p1) { return }
    const color = selected ? 'orange' : p1.get('defaultColor')
    const sizeType = selected ? 'big' : ''

    const currentStyle = p1.getStyle()
    const newStyle = this.getNewPointStyle_({ color, sizeType })
    newStyle.setZIndex(currentStyle.getZIndex())
    p1.setStyle(newStyle)
  }

  setDefaultStyle_(poleId) {
    this.setStyle_(poleId, { selected: false })
  }

  setSelectedStyle_(poleId) {
    this.setStyle_(poleId, { selected: true })
  }

  updateDefaultStyle_(pole) {
    const poleId = pole.id
    const color = this.getDefaultColor_(pole)
    const { p1 } = this.getFeaturesByPoleId_(poleId)
    p1.set('defaultColor', color)
  }

  getNewPointFeature_(point, info) {
    const feat = new ol.Feature({
      geometry: new ol.geom.Point(point)
    })
    feat.setId(info.id)
    feat.set('defaultColor', info.defaultColor)
    const style = this.getNewPointStyle_({
      color: info.defaultColor,
    })
    style.setZIndex(info.zIndex++)
    feat.setStyle(style)
    return feat
  }

  getFeaturesByPoleId_(poleId) {
    const src = this.layer.getSource()
    const p1 = src.getFeatureById(poleId)
    return { p1 }
  }

  createPoleLayer_(srcArr) {
    const info = {
      id: '',
      zIndex: 0,
      defaultColor: 'green',
    }
    const coords = []
    const feats = []
    for (const e of srcArr) {
      info.defaultColor = this.getDefaultColor_(e)
      const p1 = this.coordFromLonLat(e.point.coordinates)
      coords.push(p1)
      info.id = e.id
      feats.push(this.getNewPointFeature_(p1, info))
    }

    const layer = new ol.layer.Vector({
      source: new ol.source.Vector({ features: feats }),
    })
    layer.name = 'pole'
    layer.setZIndex = 500

    let extent = null
    if (coords.length > 0) {
      extent = ol.extent.boundingExtent(coords)
    }

    this.layer = layer
    this.layerInfo.extent = extent

    this.layerInfo.onPoleClick = ({ event, feature }) => {
      // 重なってたりする場合はそれぞれ飛んでくるので、一回で止める
      if (event.originalEvent.defaultPrevented) { return }
      event.preventDefault()
      const poleId = feature.getId()
      this.selectPoles([parseInt(poleId)], { emitEvent: true })
    }
  }

  prepareLayer(srcArr) {
    this.createPoleLayer_(srcArr)
    // 選択を空にする
    this.selectedPoles = []

    return this.getLayer()
  }

  setListeners_() {
    this.stream.recv(EVT.SELECT_POLES,
      ({ ids }) => {
        if (!ids || ids.length === 0) { return }
        this.selectPoles(ids, { emitEvent: false })
      }
    )
    this.stream.recv(EVT.UNSELECT_POLES,
      ({ ids }) => {
        if (!ids || ids.length === 0) { return }
        this.unselectPoles(ids)
      }
    )
    this.stream.recv(EVT.UPDATED_POLES,
      ({ poles }) => {
        if (!poles || poles.length === 0) { return }
        poles.forEach(pole => {
          this.updateDefaultStyle_(pole)
        })
      }
    )
  }

  unselectPoles(ids) {
    ids.forEach(e => {
      this.setDefaultStyle_(e)
    })
    this.selectedPoles = this.selectedPoles.filter(id => {
      return ids.indexOf(id) === -1
    })
  }

  selectPoles(ids, { emitEvent }) {
    const currentSelectedPoles = this.selectedPoles
    this.unselectPoles(this.selectedPoles)

    // 全部同じだったら再selectしない
    if (this.isArrayAllSame(currentSelectedPoles, ids)) {
      if (emitEvent) {
        this.stream.send(EVT.UNSELECT_POLES, { ids: ids })
      }
      return
    }

    this.selectedPoles = ids
    this.selectedPoles.forEach(e => {
      this.setSelectedStyle_(e)
    })
    if (emitEvent) {
      this.stream.send(EVT.SELECT_POLES, { ids: this.selectedPoles })
    }
  }
}
